// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; 
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig'; // 新增 CSRF 設定
import API_URL from '../config';
import ReportList from "./ReportList";
import BlockchainAcademy from "./BlockchainAcademy";
import SEO from "../components/SEO";

function Home({ darkMode }) {
  const { t } = useTranslation(); // 使用 i18n 翻譯函數
  const navigate = useNavigate(); 
  const [guestReport, setGuestReport] = useState(null); // 用於存儲報案資訊
  const [isExpanded, setIsExpanded] = useState(false); // 控制浮動視窗是否展開
  const [walletAddress, setWalletAddress] = useState("");
  const [chainId, setChainId] = useState(1); // 預設使用 Ethereum (1)
  
  const handleWalletSearch = () => {
    if (!walletAddress) return; // 可依需求加入地址驗證
    // 利用 useNavigate 將地址與鏈代號帶到 WalletRisk 頁面
    navigate("/wallet-risk", { state: { walletAddress, chainId } });
  };

  useEffect(() => {
    // 泡泡效果
    const bubble1 = document.getElementById('bubble1');
    let isMouseMoved = false;
    const handleMouseMove = (e) => {
      if (!isMouseMoved) isMouseMoved = true;
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      bubble1.style.transform = `translate(${mouseX * 0.02}px, ${mouseY * 0.02}px)`;
    };
    const handleScroll = () => {
      if (isMouseMoved) {
        const scrollY = window.scrollY;
        bubble1.style.transform = `translateY(${scrollY * 0.1}px)`;
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    // 若 localStorage 有 guestId 則從後端取得報案資訊
    const guestId = localStorage.getItem('guestId');
    if (guestId) {
      axios.get(`${API_URL}/api/guest_report/${guestId}`)
        .then((res) => {
          setGuestReport(res.data.report);
        })
        .catch((err) => {
          console.error(t('errorFetchingGuestReport'), err);
        });
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [t]);

  // 切換浮動視窗展開／收合
  const handleTogglePopup = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <SEO 
        path="/home"
        title={t('welcomeToApp')}
        description={t('appDescription')}
        keywords="DamenKYT, blockchain security, risk assessment, wallet analysis"
        ogTitle={t('welcomeToApp')}
        ogDescription={t('appDescription')}
        ogImage={darkMode ? '/images/darkHeroBanner.png' : '/images/heroBanner.png'}
        twitterCard="summary_large_image"
        twitterTitle={t('welcomeToApp')}
        twitterDescription={t('appDescription')}
        twitterImage={darkMode ? '/images/darkHeroBanner.png' : '/images/heroBanner.png'}
        canonicalUrl={window.location.href}
        robots="index, follow"
        siteName="DamenKYT"
        image={darkMode ? '/images/darkHeroBanner.png' : '/images/heroBanner.png'}
        imageWidth="1200"
        imageHeight="630"
        imageAlt={t('welcomeToApp')}
      />
      <div className={`${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-black'}`}>
        {/* Hero Banner */}
        <div 
          className="relative min-h-[50vh] bg-cover bg-center overflow-hidden" 
          style={{ backgroundImage: `url(${darkMode ? '/images/darkHeroBanner.png' : '/images/heroBanner.png'})` }}
        >
          <div
            className="absolute top-1/5 left-0 md:left-20 min-h-[300px] md:min-h-[500px] w-full md:w-1/3 flex flex-col items-center justify-center text-center px-4 md:px-0"
          >
            <img
              src="/images/Vector.png"
              alt="Circle Background"
              className="w-full h-full object-contain"
              style={{ 
                pointerEvents: "none", 
                minHeight: "450px", 
                minWidth: "450px", 
                maxHeight: "500px",
                maxWidth: "500px"
              }} 
            />
            <div className="absolute flex flex-col items-center text-center space-y-4">
              <h1 className="text-2xl md:text-4xl font-bold mb-4 text-black">
                {t('welcomeToApp')}
              </h1>
              <p className="text-md md:text-lg text-black break-words max-w-[400px] md:max-w-[400px] lg:max-w-[400px] px-4">
                {t('appDescription')}
              </p>
              <div className="flex space-x-2 md:space-x-4 relative z-10 flex-wrap justify-center">
                <button 
                  onClick={() => navigate('/report')}
                  className="bg-[#6a0dad] hover:bg-[#5a0b8a] text-white font-bold py-2 px-4 md:py-3 md:px-6 rounded"
                >
                  {t('generateReport')}
                </button>
                <button 
                  onClick={() => navigate('/services')}
                  className="border-2 border-[#6a0dad] hover:border-[#5a0b8a] text-[#6a0dad] hover:text-[#5a0b8a] font-bold py-2 px-4 md:py-3 md:px-6 rounded"
                >
                  {t('learnMore')}
                </button>
              </div>
            </div>
          </div>
          <div className="bubble-container absolute inset-0 overflow-hidden">
            <img 
              src="/images/bubble.png" 
              className="absolute inset-0 w-full h-full object-cover" 
              id="bubble1" 
              alt="Bubbles" 
            />
          </div>

          {guestReport && (
            <div
              className={`fixed top-1/2 right-0 transform -translate-y-1/2 bg-white shadow-lg rounded-l-lg overflow-hidden transition-all duration-300 ${
                isExpanded ? 'w-60 md:w-80' : 'w-12'
              }`}
            >
              <div onClick={handleTogglePopup} className="cursor-pointer h-full flex flex-col justify-center items-center">
                {isExpanded ? (
                  <div className="p-2 md:p-4">
                    <h2 className="text-lg md:text-xl font-bold mb-2">
                      {t('guestReportInfo')}
                    </h2>
                    <p>
                      <strong>{t('guestId')}:</strong> {localStorage.getItem('guestId')}
                    </p>
                    <p>
                      <strong>{t('incidentType')}:</strong> {guestReport.incidentType}
                    </p>
                    <p>
                      <strong>{t('description')}:</strong> {guestReport.description}
                    </p>
                  </div>
                ) : (
                  <div className="w-12 h-12 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 錢包地址查詢 */}
        <section className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <span className="inline-block bg-gradient-to-r from-purple-400 to-indigo-500 dark:from-purple-500 dark:to-violet-600 text-white px-4 py-1 rounded-full uppercase tracking-wide font-medium shadow-md">
                {t("walletRiskAssessment")}
              </span>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{t("walletSearchDescription")}</p>
            </div>
            <div className="flex h-12 rounded-xl shadow-md border border-purple-200/50 dark:border-purple-700/30 overflow-hidden">
              {/* 輸入地址欄位 */}
              <input
                type="text"
                placeholder={t("inputUrlOrAddress")}
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 text-black dark:text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 placeholder-gray-400 dark:placeholder-gray-500 h-full box-border leading-[2rem] transition-all"
              />
              {/* 鏈選擇下拉選單 */}
              <select
                        value={chainId}
                        onChange={(e) => setChainId(parseInt(e.target.value))}
                        className="p-3 border rounded-xl shadow-md bg-white dark:bg-gray-800 text-black dark:text-white border-purple-200/50 dark:border-purple-700/30 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
                      >
                        <option value="-3">Solana</option>
                        <option value="-2">TRX</option>
                        <option value="-1">Bitcoin</option>
                        <option value="1">Ethereum</option>
                        <option value="10">Optimism</option>
                        <option value="25">Cronos</option>
                        <option value="56">BSC</option>
                        <option value="100">Gnosis</option>
                        <option value="137">Polygon</option>
                        <option value="169">Manta Pacific</option>
                        <option value="199">Bittorrent</option>
                        <option value="250">Fantom</option>
                        <option value="288">Boba</option>
                        <option value="324">zkSync Era</option>
                        <option value="1024">CLV</option>
                        <option value="1101">Polygon zkEVM</option>
                        <option value="1111">Wemix</option>
                        <option value="1284">Moonbeam</option>
                        <option value="1285">Moonriver</option>
                        <option value="5000">Mantle</option>
                        <option value="8453">Base</option>
                        <option value="42161">Arbitrum</option>
                        <option value="42220">Celo</option>
                        <option value="43114">Avalanche</option>
                        <option value="59144">Linea</option>
                        <option value="81457">Blast</option>
                        <option value="1313161554">Aurora</option>
                      </select>
              {/* 搜尋按鈕 */}
              <button
                onClick={handleWalletSearch}
                className="px-6 bg-gradient-to-r from-purple-400 to-indigo-500 dark:from-purple-500 dark:to-violet-600 text-white font-bold rounded-r-xl hover:from-purple-500 hover:to-indigo-600 dark:hover:from-purple-600 dark:hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 h-full box-border leading-[2rem] flex items-center justify-center transition-all"
              >
                {t("search")}
              </button>
            </div>
          </div>
        </section>

        {/* 近期通報可疑名單 */}
        <section className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-indigo-500 bg-clip-text text-transparent dark:from-purple-300 dark:to-violet-400 uppercase tracking-wide">
              {t('recentReports')}
            </h2>
            <div className="w-20 h-1 mx-auto mb-8 bg-gradient-to-r from-purple-300 to-indigo-400 rounded-full dark:from-purple-500 dark:to-violet-600" />
          </div>
          <div className="max-w-6xl mx-auto" style={{ height: '500px', overflowY: 'auto' }}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-purple-200/50 dark:border-purple-700/30 p-4">
              <ReportList />
            </div>
          </div>
        </section>

        {/* 區塊鏈學院 */}
        <section className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-indigo-500 bg-clip-text text-transparent dark:from-purple-300 dark:to-violet-400 uppercase tracking-wide">
              {t('blockchainAcademyTitle')}
            </h2>
            <div className="w-20 h-1 mx-auto mb-8 bg-gradient-to-r from-purple-300 to-indigo-400 rounded-full dark:from-purple-500 dark:to-violet-600" />
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-purple-200/50 dark:border-purple-700/30 p-6">
              <BlockchainAcademy hideBanner={true} />
            </div>
          </div>
        </section>

        {/* 為什麼使用我們的服務 */}
        <section className={`${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-black'} py-12 px-4`}>
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold mb-4">
                {t('whyUseApp')}
              </h2>
              <p className="mb-6">
                {t('whyUseAppDescription')}
              </p>
              <div className="flex space-x-4">
                <button 
                  onClick={() => navigate('/report')}
                  className="bg-[#6a0dad] hover:bg-[#5a0b8a] text-white font-bold py-2 px-4 rounded"
                >
                  {t('generateReport')}
                </button>
                <button 
                  onClick={() => navigate('/services')}
                  className="border-2 border-[#6a0dad] hover:border-[#5a0b8a] text-[#6a0dad] hover:text-[#5a0b8a] font-bold py-2 px-4 rounded"
                >
                  {t('learnMore')}
                </button>
              </div>
            </div>
            <div className="md:w-1/2 mt-8 md:mt-0">
              <img
                src="/home_whyuse_img.png"
                alt="Why Use Our Service"
                className="w-full h-auto mt-6 rounded-lg"
              />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;
